import { useRepositorySuspenseQuery } from '@mews-commander/data-access';
import { includesLowerCase } from '../../../common/utils';
import { TemplatesTableFilterValues } from '../TemplatesTableFilters';
import { TemplatesTableData } from '../types';

type ServiceId = string;
type ServiceName = string;

interface UseFilterTemplatesTableDataProps {
    filters?: TemplatesTableFilterValues;
    tableSearchValue?: string;
    serviceNamesById: Record<ServiceId, ServiceName>;
}

const filterBySearchValue = ({
    data,
    searchValue,
    serviceNamesById,
}: {
    data: TemplatesTableData;
    searchValue: string;
    serviceNamesById: Record<ServiceId, ServiceName>;
}): TemplatesTableData =>
    data.filter((template) => {
        const { name, status, serviceIds } = template;
        return (
            includesLowerCase(name, searchValue) ||
            includesLowerCase(status, searchValue) ||
            serviceIds.some((id) => includesLowerCase(serviceNamesById[id], searchValue))
        );
    });

const filterByFilters = ({
    data,
    filters,
}: {
    data: TemplatesTableData;
    filters: TemplatesTableFilterValues;
}): TemplatesTableData => {
    const { name: filterName, service: filterService, status: filterStatus } = filters;
    return data.filter((template) => {
        const { name, status, serviceIds } = template;
        const matchesName = filterName == null || filterName === '' || includesLowerCase(name, filterName);
        const matchesService = filterService == null || serviceIds.some((id) => filterService?.includes(id));
        const matchesStatus = filterStatus == null || filterStatus?.includes(status);
        return matchesName && matchesService && matchesStatus;
    });
};

export const useFilteredTemplatesTableData = ({
    filters,
    tableSearchValue,
    serviceNamesById,
}: UseFilterTemplatesTableDataProps): TemplatesTableData => {
    const { data: contractTemplatesData } = useRepositorySuspenseQuery('reservationContractTemplates', 'getAll', {});
    const contractTemplatesTableData: TemplatesTableData =
        contractTemplatesData.templates?.map((template) => ({
            id: template.id,
            name: template.name,
            serviceIds: template.services.map((service) => service.id),
            status: template.status,
        })) ?? [];

    const dataFilteredByFilters =
        filters != null ? filterByFilters({ data: contractTemplatesTableData, filters }) : contractTemplatesTableData;

    return tableSearchValue != null
        ? filterBySearchValue({ data: contractTemplatesTableData, searchValue: tableSearchValue, serviceNamesById })
        : dataFilteredByFilters;
};
